/**
 * Sport configuration context.
 *
 * Fetches `/api/sport-config/current` once per session for the logged-in user
 * and exposes the effective sport, positions, session_types and icon resolver.
 *
 * Icon strings (e.g. "Activity", "Swords") returned by the backend are mapped
 * to actual Lucide icon components inside this file — keeps the rest of the
 * app dependency-free of the sport_config schema.
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
  Activity, Heart, Swords, Dumbbell, ShieldCheck, Users, Target, ShieldAlert,
  RotateCcw, MoreHorizontal, Sparkles, ArrowUp, Gamepad2, Flag, Brain, Zap,
  CircleDot, Bike, Gauge, Bell, Calendar, ClipboardList, AlertCircle,
} from 'lucide-react';
import { useAuth } from './AuthContext';

const SportContext = createContext(null);

// String → Lucide component map. Add new icon names here when extending sport defaults.
const ICONS = {
  Activity, Heart, Swords, Dumbbell, ShieldCheck, Users, Target, ShieldAlert,
  RotateCcw, MoreHorizontal, Sparkles, ArrowUp, Gamepad2, Flag, Brain, Zap,
  CircleDot, Bike, Gauge, Bell, Calendar, ClipboardList, AlertCircle,
};

export const resolveIcon = (name) => ICONS[name] || Activity;

const FALLBACK = {
  sport: 'football',
  sport_label: 'Football',
  positions: ['Goalkeeper', 'Defender', 'Midfielder', 'Forward'],
  session_types: [
    { value: 'TRAINING', label: 'Training', icon: 'Activity', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
    { value: 'MATCH', label: 'Match', icon: 'Swords', color: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
    { value: 'GYM', label: 'Gym', icon: 'Dumbbell', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
    { value: 'RECOVERY', label: 'Recovery', icon: 'Heart', color: 'bg-sky-500/10 text-sky-400 border-sky-500/20' },
    { value: 'OTHER', label: 'Other', icon: 'MoreHorizontal', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
  ],
  is_custom: false,
};

export const SportProvider = ({ children }) => {
  const { user, api } = useAuth();
  const [config, setConfig] = useState(FALLBACK);
  const [isLoading, setIsLoading] = useState(false);

  const fetchConfig = useCallback(async () => {
    if (!user) {
      setConfig(FALLBACK);
      return;
    }
    setIsLoading(true);
    try {
      const res = await api.get('/sport-config/current');
      if (res?.data) setConfig({ ...FALLBACK, ...res.data });
    } catch {
      setConfig(FALLBACK);
    }
    setIsLoading(false);
  }, [user, api]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const sessionTypeMap = useMemo(() => {
    const m = {};
    (config.session_types || []).forEach(s => { m[s.value] = s; });
    return m;
  }, [config.session_types]);

  const getSessionTypeMeta = useCallback(
    (value) => sessionTypeMap[value] || {
      value, label: value, icon: 'Activity',
      color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
    },
    [sessionTypeMap],
  );

  return (
    <SportContext.Provider
      value={{
        sport: config.sport,
        sportLabel: config.sport_label,
        loadDefaults: config.load_defaults,
        positions: config.positions || [],
        sessionTypes: config.session_types || [],
        isCustom: !!config.is_custom,
        isLoading,
        refresh: fetchConfig,
        getSessionTypeMeta,
        resolveIcon,
      }}
    >
      {children}
    </SportContext.Provider>
  );
};

export const useSport = () => {
  const ctx = useContext(SportContext);
  if (!ctx) throw new Error('useSport must be used within SportProvider');
  return ctx;
};
